<template>
    <div v-if="!isLoading && flowStore.currentFlow">
        <div class="container">

            <!-- SECTION: status + title + actions -->
            <div class="section-head" :style="{ '--accent': '#4F7EB3' }">
                <div class="head-left">
                    <h5 class="title">{{ flowStore.currentFlow.FlowName }}</h5>

                    <div class="chip-row">
                    <span class="chip" :class="serverConnection ? 'chip-ok' : 'chip-warn'">
                        <i class="bi" :class="serverConnection ? 'bi-wifi' : 'bi-wifi-off'"></i>
                        {{ serverConnection ? $t('cameras.edgalyticConnected') : $t('cameras.edgalyticDisconnected') }}
                    </span>

                    <span class="chip" :class="streamStatus ? 'chip-ok' : 'chip-warn'">
                        <i class="bi" :class="streamStatus ? 'bi-activity' : 'bi-slash-circle'"></i>
                        {{ streamStatus ? $t('cameras.detectionOn') : $t('cameras.detectionOff') }}
                    </span>

                    <span class="chip" :class="streamChip.toneClass" :title="streamChip.tooltip || ''">
                        <i class="bi" :class="streamChip.icon"></i>
                        {{ $t('cameras.overview.streamChip', { status: streamChip.label }) }}<template v-if="streamChip.durationText"> · {{ streamChip.durationText }}</template>
                    </span>
                    <span v-if="streamChip.guidance" style="color:#81909a; font-size:12px; align-self:center;" :title="streamChip.detail || ''">{{ streamChip.guidance }}</span>
                    </div>
                </div>

                <!-- RIGHT: page actions -->
                <div class="head-actions">
                    <!-- Primary CTA: Start/Stop -->
                    <button
                    v-if="!userStore.isRegularUser && !streamStatus"
                    type="button"
                    class="btn-solid ok head-cta"
                    @click="startDetection"
                    :disabled="isLoading || !socket"
                    :title="$t('cameras.flowOutput.startDetection')"
                    >
                    <i class="bi bi-play-btn-fill"></i> {{ $t('cameras.flowOutput.startDetection') }}
                    </button>

                    <button
                    v-else-if="!userStore.isRegularUser && streamStatus"
                    type="button"
                    class="btn-solid danger head-cta"
                    @click="stopDetection"
                    :disabled="isLoading || !socket"
                    :title="$t('cameras.flowOutput.stopDetection')"
                    >
                    <i class="bi bi-stop-btn-fill"></i> {{ $t('cameras.flowOutput.stopDetection') }}
                    </button>

                    <!-- Secondary actions -->
                    <button v-if="socket" class="btn-ghost" @click="toggleLiveStream">
                    <i class="bi" :class="isLiveStreamOpen ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
                    {{ isLiveStreamOpen ? $t('cameras.flowOutput.closeLiveStream') : $t('cameras.flowOutput.openLiveStream') }}
                    </button>

                    <router-link
                    v-if="!isSmallScreen && userStore.currentCompany.role !== 'User' && flowStore.currentFlow.FlowId"
                    :to="`/flow_settings/${flowStore.currentFlow.FlowId}`"
                    class="btn-outline"
                    type="button"
                    >
                    <i class="bi bi-gear"></i> {{ $t('cameras.overview.settings') }}
                    </router-link>
                </div>
            </div>

            <!-- Collapsible live stream -->
            <div v-if="socket" class="live-stream-wrapper">
                <transition name="roll">
                    <div v-if="isLiveStreamOpen" class="live-stream-frame">
                        <live-stream
                            :stream-link="cameraStreamUrl"
                            :flow-id="flowId"
                            :stream-id="streamId"
                            :stream-status="streamStatus"
                            :socket="socket"
                            @streamstatusChange="handleStreamStatus"
                        />
                    </div>
                </transition>
            </div>

            <!-- CARD: page body -->
            <div class="card">
                <div class="card-body">

                    <!-- ONE wrapper controls the column width for BOTH grids -->
                    <div class="kpi-stage">
                        <!-- Filter / sticky control bar spans full width -->
                        <div class="sticky-filter kpi-span-full" style="margin-bottom: 12px;">
                            <div class="top-row" style="padding-bottom:8px;">
                                <div class="form-switch data-mode-switch" style="padding-left: 0px;">
                                    <input class="form-check-input" type="checkbox" id="dataModeSwitch" v-model="isTodaySelected">
                                    <label class="form-check-label" for="dataModeSwitch">
                                        {{ isTodaySelected ? $t('cameras.flowOutput.todayRealTime') : $t('cameras.flowOutput.historical') }}
                                    </label>
                                </div>
                            </div>

                            <div v-if="!isTodaySelected" class="top-row" style="margin-top: 10px;">
                                <duration-dropdown
                                :dateRangeMode="dateRangeMode"
                                :selectedDuration="duration"
                                @updateDateRangeMode="changeDateRangeMode"
                                @updateDuration="changeDuration"
                                @clearDatePicker="updateClearDatePicker"
                                />
                                <date-picker
                                :clear-date-picker="clearDatePicker"
                                @updateStartDate="updateStartDate"
                                @updateEndDate="updateEndDate"
                                @updateDateRangeMode="changeDateRangeMode"
                                @updateClearDatePicker="updateClearDatePicker"
                                />
                                <dropdown-opening-hours-mode
                                :openingHoursMode="openingHoursMode"
                                :disabledModes="disabledOpeningHourModes"
                                @updateOpeningHoursMode="updateOpeningHoursMode"
                                />
                            </div>
                        </div>

                        <!-- Section: Live activity — only meaningful for polygon-bearing
                             zones whose dwell is meaningful (NOT Cash Register, NOT
                             Entrance / Exit / Combined EE, NOT tripwire-only). -->
                        <section
                            v-if="isTodaySelected && liveActivityZones.length"
                            class="kpi-section tone-live"
                            style="margin-top:16px;"
                        >
                            <h6 class="section-head">
                                <i class="bi bi-broadcast section-icon"></i>
                                {{ $t('cameras.flowOutput.liveActivity') }}
                            </h6>
                            <div class="kpi-grid tight">
                                <div
                                    v-for="zone in liveActivityZones"
                                    :key="zone.DetectionZoneName"
                                    class="kpi-card kpi-accent kpi-live"
                                    :style="{ '--accent': getColorForZone(zone.DetectionZoneName) }"
                                    :class="{ 'greyed-out': !streamStatus }"
                                >
                                    <div class="kpi-head">
                                        <p class="kpi-title">
                                            <b :style="{color: getColorForZone(zone.DetectionZoneName)}">{{ zone.DetectionZoneName }}</b>
                                        </p>
                                        <span class="kpi-sub">{{ $t('cameras.flowOutput.current') }}</span>
                                    </div>
                                    <div class="kpi-value">
                                        <span class="kpi-number">{{ streamStatus ? (zoneCounters[zone.DetectionZoneName] ?? 0) : $t('cameras.notAvailable') }}</span>
                                    </div>
                                </div>
                            </div>
                        </section>

                        <counters
                            class="cards-container"
                            v-if="flowStore.currentFlow.DetectionZones"
                            style="margin-top:8px;"
                            :flow-id="flowId"
                            :start-date="startDate"
                            :end-date="endDate"
                            :duration="duration"
                            :is-today-selected="isTodaySelected"
                            :date-range-mode="dateRangeMode"
                            :opening-hours-mode="openingHoursMode"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, onBeforeMount, onBeforeUnmount, onMounted, watchEffect, watch, nextTick, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { useToast } from 'vue-toastification'
import { useI18n } from 'vue-i18n'

import { detection_zone_colors_by_name_solid } from '@/constants.js'
import { parseStoredTripwires } from '@/utils/tripwireShape'
import { useFlowStore } from '@/stores/FlowStore'
import { useCompanyStore } from '@/stores/CompanyStore'
import { parseDeviceUrl } from '@/api/deviceUrl'
import { useUserStore } from '@/stores/UserStore'
import { useCameraStreamStore } from '@/stores/CameraStreamStore'
import { useDetectionZoneStore } from '@/stores/DetectionZone'
import { useOpeningHoursStore } from '@/stores/OpeningHoursStore'

import { useGlobalState } from '@/composables/useGlobalState'
import { io } from 'socket.io-client'
import { deriveStreamChip } from '@/composables/streamStatus'

import LiveStream from './LiveStream.vue'
import Counters from './Counters.vue'
import DatePicker from './DatePicker.vue'
import DurationDropdown from './DurationDropdown.vue'
import DropdownOpeningHoursMode from './DropdownOpeningHoursMode.vue'

export default {
    components: {
        LiveStream,
        Counters,
        DurationDropdown,
        DatePicker,
        DropdownOpeningHoursMode,
    },

    setup() {
        const socket = ref(null)
        const isConnectingSocket = ref(false)
        const toast = useToast()
        const { t } = useI18n()

        const { serverConnection } = useGlobalState()
        const cameraHeartBeat = ref({})
        const heartbeatTimeouts = ref({})

        // Latest rich `stream_status` per flowId (new supervisor-equipped devices only).
        const streamRichStatus = ref({})

        const activeTab = ref('chart-view')

        const flowStore = useFlowStore()
        const companyStore = useCompanyStore()
        const userStore = useUserStore()
        const CameraStreamStore = useCameraStreamStore()
        const DetectionZoneStore = useDetectionZoneStore()
        const openingHoursStore = useOpeningHoursStore()
        const route = useRoute()
        const router = useRouter()

        const flowId = computed(() => parseInt(route.params.id))
        const streamId = ref(0)
        const entranceCam = ref(false)
        const cameraStreamUrl = ref(null)
        const streamStatus = ref(null)

        // Stream-health chip: rich supervisor status when present, heartbeat watchdog otherwise.
        const streamChip = computed(() => {
            const rich = streamRichStatus.value[flowId.value]
            return deriveStreamChip({
                dbStreamStatus: streamStatus.value,
                richStatus: rich || null,
                richStatusAt: rich?._at ?? null,
                heartbeatAlive: !!cameraHeartBeat.value[flowId.value],
            })
        })

        const isLiveStreamOpen = ref(false)

        const isTodaySelected = ref(true)

        const zoneCounters = ref({})
        const detectionZones = ref([])
        const detectionZoneNames = ref({})

        const dateRangeMode = ref('duration')

        const clearDatePicker = ref(null)
        const startDate = ref(null)
        const endDate = ref(null)
        const dataGranularity = ref('daily')
        const duration = ref()
        const openingHoursMode = ref('only_regular_opening_hours')
        const disabledOpeningHourModes = ref([])

        const resetSignal = ref(null)

        const openChartViewStatus = ref(false)
        const openSmartAnalyticsStatus = ref(false)
        const openTableViewStatus = ref(false)
        const isTableTabEventList = ref(false)
        const chartData = ref({})

        const isSmallScreen = ref(window.innerWidth <= 768)

        const isLoading = ref(false)

        const detectNetworkContext = async (deviceURL) => {
            try {
                const controller = new AbortController()
                const signal = controller.signal
                const timeout = 5000 // 5 seconds timeout for checking public IP

                const fetchPromise = fetch(parseDeviceUrl(deviceURL).httpBase, { signal })
                setTimeout(() => controller.abort(), timeout)

                const response = await fetchPromise
                if (response.ok) {
                    return deviceURL
                }
            } catch (error) {
                console.error('Device Socket URL is not accessible')
                toast.error(t('cameras.deviceNotAccessible'))
                throw new Error('Device Socket URL is not accessible')
            }
            throw new Error('Device Socket URL is not accessible')
        }

        const establishSocketConnection = async (deviceURL) => {
            if (socket.value !== null) {
                console.log("Returning existing socket")
                return socket.value
            }

            if (isConnectingSocket.value) {
                console.log('Already connecting, waiting...')
                while (isConnectingSocket.value) {
                    await new Promise(r => setTimeout(r, 500))
                }
                return socket.value
            }

            console.log("Connecting socket ...")
            isConnectingSocket.value = true

            try {
                const url = await detectNetworkContext(deviceURL)
                //console.log('Network context detected, URL:', url)

                // io(url) would read "/device/<id>" as a NAMESPACE, not a path prefix — it
                // would ask for a namespace the edge does not define, over a socket.io path
                // nginx does not route. Origin and path must be passed separately.
                // For the legacy ":500x" shape these are the whole URL and "/socket.io",
                // i.e. exactly what io(url) resolved to on its own. See @/api/deviceUrl.
                const { origin, socketPath, proxied } = parseDeviceUrl(url)

                socket.value = io(origin, {
                    path: socketPath,
                    // Same-origin once this store is cut over, so this is a no-op that
                    // records the intent. NOT unconditional: on the legacy cross-origin
                    // shape the edge replies "Access-Control-Allow-Origin: *", which a
                    // browser REFUSES for a credentialed request — it would break the
                    // live view of every store not yet cut over.
                    withCredentials: proxied,
                    transports: ['websocket'],
                    reconnection: true,
                    // Infinity, not 5: after 5 attempts (~5s) socket.io gives up permanently and only a
                    // page reload recovers. A tunnel drop legitimately lasts up to ~60s while sshd frees
                    // the device's 500x port. See NavSidebar.vue for the full note.
                    reconnectionAttempts: Infinity,
                    reconnectionDelay: 1000,
                    reconnectionDelayMax: 10000,
                    debug: true,
                    perMessageDeflate: false,
                    timeout: 120000,
                    maxPayload: 50 * 1024 * 1024,
                })

                setupSocketEvents(socket.value)

                return socket.value

            } catch (error) {
                console.error('Error establishing socket connection:', error)
                throw error
            } finally {
                console.log('Resetting serverConnection flag')
                isConnectingSocket.value = false
            }
        }

        const setupSocketEvents = (socketInstance) => {
            socketInstance.on('zone_counter', (data) => {
                if (String(data.stream_id) === String(streamId.value)) {
                    for (const zone in data.zone_counter) {
                        zoneCounters.value[zone] = data.zone_counter[zone] // Update the existing keys in zoneCounters
                    }
                }
            })

            socketInstance.on('heartbeat', (data) => {
                const { streamId, heartbeat } = data

                const cameraStream = CameraStreamStore.inputcamerastream

                if (cameraStream.CameraStreamId === Number(streamId)) {
                    
                    if (heartbeat && flowId.value) {
                        cameraHeartBeat.value[flowId.value] = true
                        clearTimeout(heartbeatTimeouts.value[flowId.value])
                        heartbeatTimeouts.value[flowId.value] = setTimeout(() => {
                            cameraHeartBeat.value[flowId.value] = false
                        }, 5000)
                    }
                }
            })

            // Rich health from the new edge stream-supervisor (new devices only).
            socketInstance.on('stream_status', (data) => {
                const cameraStream = CameraStreamStore.inputcamerastream
                if (cameraStream.CameraStreamId === Number(data.streamId) && flowId.value) {
                    streamRichStatus.value[flowId.value] = { ...data, _at: Date.now() }
                }
            })

            socketInstance.on('disconnection_message', (data) => {
                console.log('message on disconnection: ', data)
            })

            socketInstance.on('error', (error) => {
                console.error('Error received from server:', error)
                toast.error(t('cameras.error', { message: error.message }))
            })
        }

        onBeforeMount(async () => {
            // 1) fetch user + company once, bail if no company selected
            const user = await userStore.fetchUserDetails()
            const cid  = userStore.currentCompany?.CompanyId
            if (!cid) {
                // no tenant yet — nothing to socket-connect
                return
            }

            await companyStore.fetchCompanyDetails(cid)

            // Fetch opening hours
            const isEmpty = await openingHoursStore.fetchOpeningHours()
            if (isEmpty) {
                openingHoursMode.value = 'all'
                disabledOpeningHourModes.value = [
                    'only_regular_opening_hours',
                    'regular_and_irregular_opening_hours'
                ]
            }

            // 2) only call establishSocketConnection if there really *is* a URL
            const url = companyStore.company?.SocketioDeviceURL
            if (!url) {
                // either first load or just no device configured
                return
            }

            try {
                socket.value = await establishSocketConnection(url)
                console.log('Socket connection Flow Output established')

                window.addEventListener('resize', handleResize)
                handleResize() // Set initial state based on current window size
            
            } catch (error) {
                console.error('Failed to establish Flow Output socket connection::', error)
            }
        })


        const loadData = async () => {
            // Load your data here based on the flowId
            try {
                // Reset zoneCounters and detectionZones before loading new data
                zoneCounters.value = {}
                detectionZones.value = []

                await flowStore.fetchCurrentFlow(parseInt(route.params.id))

                await CameraStreamStore.fetchInputCameraStreambyFlowId(flowId.value)
                detectionZones.value = await DetectionZoneStore.fetchDetectionZonesByFlowId(flowId.value)

                // Force Vue to recognize the update before assigning values
                await nextTick() // Ensures reactivity catches the update

                // Set up local state based on fetched flow data
                cameraStreamUrl.value = flowStore.currentFlow.CameraStreamLink
                detectionZoneNames.value = flowStore.currentFlow.DetectionZones

                // Initialize zone counters
                for (const zone in detectionZoneNames.value) {
                    zoneCounters.value[zone] = 0 // Initialize with 0
                }

                // Set initial duration
                changeDuration(30)

                // Watch stream status
                watchEffect(() => {
                    if (CameraStreamStore.inputcamerastream.StreamStatus !== undefined) {
                        streamStatus.value = CameraStreamStore.inputcamerastream.StreamStatus
                        streamId.value = CameraStreamStore.inputcamerastream.CameraStreamId
                    }
                })
            } catch (error) {
                if (error.message === 'FLOW_NOT_FOUND') {
                    toast.error(t('cameras.flowOutput.notAllowed'))
                    return router.push('/')
                }
                console.error("Error initializing FlowOutput component:", error)
            }
        }

        // Watch for changes in flow ID and reload data if it changes
        watch(flowId, async () => {
            await loadData()
        })

        watch(() => isTodaySelected.value, (newValue) => {
            if (newValue) {
                //await loadData() // Reload data when the route ID changes
            } else {
                duration.value = 30
            }
        })

        onMounted(async () => {
            isLoading.value = true
            try {
                loadData()
            } finally {
                isLoading.value = false
            }
            
        })

        const toggleLiveStream = () => {
            isLiveStreamOpen.value = !isLiveStreamOpen.value
        }

        const startDetection = () => {
            streamStatus.value = true
            const streamDetails = createStreamDetails(streamId.value, cameraStreamUrl.value, entranceCam.value, detectionZones.value)
            socket.value.emit('start_stream', streamDetails)
            
            CameraStreamStore.editInputCameraStream(CameraStreamStore.inputcamerastream.CameraStreamId, streamStatus.value)
        }

        const stopDetection = () => {
            streamStatus.value = false
            const streamDetails = createStreamDetails(streamId.value, cameraStreamUrl.value, entranceCam.value, detectionZones.value)
            socket.value.emit('stop_stream', streamDetails)
            
            CameraStreamStore.editInputCameraStream(CameraStreamStore.inputcamerastream.CameraStreamId, streamStatus.value)
        }

        const createStreamDetails = (streamId, streamLink, entranceCam, detectionZones) => {
            // A zone may carry a polygon (DetectionZonePointArray) and/or one or
            // more tripwires (DetectionZoneTripwirePoints, in the current
            // [{id, points}, ...] storage shape). Either field can be NULL on the
            // backend.
            const safeParsePolygon = (str) => {
                if (!str) return null
                if (typeof str !== 'string') return str
                try { return JSON.parse(str) } catch { return null }
            }
            const roundPoints = (pts) => pts.map(point => point.map(coord => Math.round(coord)))
            const roundTripwires = (tws) => tws.map(tw => ({
                id: tw.id,
                points: roundPoints(tw.points),
            }))

            const detection_zones = {}
            const tripwires = {}

            for (const zone of detectionZones) {
                const zoneName = zone.DetectionZoneName

                const polygon = safeParsePolygon(zone.DetectionZonePointArray)
                if (Array.isArray(polygon) && polygon.length >= 3) {
                    detection_zones[zoneName] = roundPoints(polygon)
                }

                // parseStoredTripwires tolerates all historical shapes and always
                // returns [{id, points: [[x,y],[x,y]]}, ...] — what the edge socket
                // handler now expects, matching its DB load path.
                const tws = parseStoredTripwires(zone.DetectionZoneTripwirePoints)
                if (tws.length > 0) {
                    tripwires[zoneName] = roundTripwires(tws)
                }
            }

            return {
                stream_id: streamId,
                stream_url: streamLink,
                entrance_cam: entranceCam,
                detection_zones: detection_zones,
                tripwires: tripwires,
            }
        }

        const handleStreamStatus = (data) => {
            streamStatus.value = data
        }

        const openChartView = () => {
            openChartViewStatus.value = true
            openTableViewStatus.value = false
            openSmartAnalyticsStatus.value = false
        }

        const handleChartData = (data) => {
            chartData.value = data
        }

        const changeDuration = (newDuration) => {
            if (!isTodaySelected.value){
                duration.value = newDuration
            }
        }

        const changeDateRangeMode = (newMode) => {
            dateRangeMode.value = newMode
        }

        const updateOpeningHoursMode = (newMode) => {
            openingHoursMode.value = newMode
        }

        const updateClearDatePicker = (newValue) => {
            clearDatePicker.value = newValue
        }
        
        const updateStartDate = (date) => {
            startDate.value = date
        }

        const updateEndDate = (date) => {
            endDate.value = date
        }

        const updateDataGranularity = (granularity) => {
            dataGranularity.value = granularity
        }

        const activateResetSignal = (newValue) => {
            resetSignal.value = newValue
        }

        const openSmartAnalyticsView = () => {
            openSmartAnalyticsStatus.value = true
            openChartViewStatus.value = false
            openTableViewStatus.value = false
        }

        const openTableView = () => {
            openTableViewStatus.value = true
            openChartViewStatus.value = false
            openSmartAnalyticsStatus.value = false
        }

        const handleTabStatusChange = (status) => {
            isTableTabEventList.value = status
        }

        const getColorForZone = (zoneName) => {
            const zoneType = detectionZoneNames.value[zoneName]?.type
            return detection_zone_colors_by_name_solid[zoneType] || 'black'
        }

        // "Live activity" only makes sense for polygon-bearing zones whose dwell
        // semantics are meaningful — i.e. NOT Cash Register, NOT Entrance / Exit /
        // Combined EE (these are about directional flow, not lingering inside a
        // polygon area), and NOT tripwire-only zones (no polygon → no population
        // to count).
        const ENTRANCE_LIKE_TYPE_IDS = [1, 2, 3]
        const safeParseList = (str) => {
            if (str === null || str === undefined) return null
            if (typeof str !== 'string') return str
            try { return JSON.parse(str) } catch { return null }
        }
        const zoneHasPolygon = (zone) => {
            const pts = safeParseList(zone?.DetectionZonePointArray)
            return Array.isArray(pts) && pts.length >= 3
        }
        // Sorted by zone name to match the Counters sections below (Zone entries /
        // Average dwell time / Crossings). This grid reads its zones from the DB
        // row order while those read theirs from the API response's key order, so
        // without a shared sort the same zones sat in different positions in each
        // section and could not be read across.
        const liveActivityZones = computed(() =>
            detectionZones.value.filter((zone) => {
                if (!zone) return false
                if (zone.DetectionZoneTypeId === 999) return false
                if (ENTRANCE_LIKE_TYPE_IDS.includes(zone.DetectionZoneTypeId)) return false
                return zoneHasPolygon(zone)
            }).sort((a, b) => (a.DetectionZoneName || '').localeCompare(b.DetectionZoneName || ''))
        )

        const handleResize = () => {
            isSmallScreen.value = window.innerWidth <= 768
        }

        onBeforeUnmount(() => {
            // Optionally clear data when component unmounts
            zoneCounters.value = {}
            detectionZones.value = []
            
            if (socket.value) {
                socket.value.off('zone_counter')
                socket.value.off('heartbeat')
                socket.value.off('stream_status')
                socket.value.off('error')
                socket.value.off('connect')
                socket.value.disconnect()
            }
            Object.values(heartbeatTimeouts.value).forEach(clearTimeout)

            window.removeEventListener('resize', handleResize)
        })

        return {
            serverConnection,
            cameraHeartBeat,
            activeTab,
            flowStore,
            userStore,
            flowId,
            streamId,
            cameraStreamUrl,
            streamStatus,
            streamChip,
            startDetection,
            stopDetection,
            handleStreamStatus,
            toggleLiveStream,
            isLiveStreamOpen,
            socket,
            isTodaySelected,
            changeDuration,
            changeDateRangeMode,
            updateOpeningHoursMode,
            openingHoursMode,
            disabledOpeningHourModes,
            updateClearDatePicker,
            clearDatePicker,
            updateStartDate,
            updateEndDate,
            updateDataGranularity,
            startDate,
            endDate,
            dataGranularity,
            duration,
            dateRangeMode,
            zoneCounters,
            detectionZones,
            getColorForZone,
            liveActivityZones,
            activateResetSignal,
            resetSignal,
            openChartView,
            openChartViewStatus,
            handleChartData,
            chartData,
            isLoading,
            openSmartAnalyticsView,
            openSmartAnalyticsStatus,
            openTableView,
            openTableViewStatus,
            handleTabStatusChange,
            isTableTabEventList,
            isSmallScreen
        }
    }
}

</script>

<style scoped>
/* Active tab with red background and white text */
.nav-tabs {
  --bs-nav-tabs-link-active-color: #ffffff !important;
  --bs-nav-tabs-link-active-bg: #4F7EB3 !important;
  --bs-nav-tabs-border-color: #000000 !important;
  --bs-nav-tabs-link-hover-border-color: #000000 !important;
}
.nav {
  --bs-nav-tabs-link-active-border-color: #000000 !important;
  --bs-nav-link-hover-color: #4F7EB3 !important;
  --bs-nav-link-color: #425461 !important;
}
.card-title{ color:#4F7EB3 !important }

/* --- Section Head --- */
.section-head{
  display:flex; justify-content:space-between; gap:14px; flex-wrap:wrap;
  border-left:4px solid var(--accent,#4F7EB3);
  background:#fff; border:1px solid #e6edf3; border-radius:12px;
  padding:10px 12px; margin:6px 0 12px;
  box-shadow:0 3px 12px rgba(13,38,76,.06);
}
.section-head .title{ margin:0; font-weight:700; color:#425461 }
.head-left{ display:flex; flex-direction:column; gap:8px }
.head-actions{ display:flex; align-items:center; gap:8px; flex-wrap:wrap }

/* Chips */
.chip-row{ display:flex; gap:8px; flex-wrap:wrap }
.chip{
  display:inline-flex; align-items:center; gap:6px;
  border-radius:999px; padding:4px 10px; font-size:.78rem;
  border:1px solid #e6edf3; color:#4F7EB3; background:#f7fbff;
}
.chip-ok{ color:#2c7a3f; background:#f3fbf5; border-color:#d9f1df }
.chip-warn{ color:#b66a00; background:#fff7e6; border-color:#ffe7bf }
.chip-muted{ color:#81909a; background:#f3f5f7 }

/* Buttons */
.btn-ghost{
  background:#f7fbff; color:#4F7EB3; border:1px solid #e6edf3;
  border-radius:8px; padding:6px 12px; cursor:pointer;
}
.btn-ghost:hover{ background:#eef6ff }
.btn-outline{
  display:inline-flex; align-items:center; gap:8px;
  border:1px solid #4F7EB3; color:#4F7EB3; padding:6px 12px;
  border-radius:8px; text-decoration:none;
}
.btn-outline:hover{ background:#4F7EB3; color:#fff }
.btn-solid{
  border:0; border-radius:8px; padding:8px 14px; color:#fff; cursor:pointer;
}
.btn-solid.ok{ background:#6C9046 }
.btn-solid.ok:disabled{ background:#95b376 }
.btn-solid.danger{ background:#C21500 }
.btn-solid.danger:disabled{ background:#de8f85 }

/* Live stream */
.live-stream-wrapper{ width:100% }
.live-stream-frame{
  margin-bottom:12px; border:1px solid #e6edf3; border-radius:12px;
  background:#fafafa; padding:8px;
}

/* Sticky controls panel */
.panelish{
  background:#fff; border:1px solid #e6edf3; border-radius:12px;
  padding:10px 12px; box-shadow:0 3px 12px rgba(13,38,76,.06);
}
.sticky-section{
  position:sticky; top:0; z-index:5; margin-bottom:14px;
  background:#fff;
}
.top-row{ display:flex; flex-wrap:wrap; gap:16px; align-items:center }
.form-check-label,.form-check-input{ margin:0; display:flex; align-items:center }
.data-mode-switch{ font-size:14px }
.form-check-input{ margin-right:6px }

/* ========== KPI Grid & Card (shared) ========== */
.kpi-grid{
  /* one responsive grid for all KPI blocks */
  --kpi-gap: 16px;
  --kpi-min: 220px; /* default pill width */
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(var(--kpi-min), 1fr));
  gap: var(--kpi-gap);
  align-items: stretch;
}

@media (min-width: 1600px){ .kpi-grid{ --kpi-min: 260px } }
@media (max-width: 1200px){ .kpi-grid{ --kpi-min: 200px } }
@media (max-width: 600px) { .kpi-grid{ --kpi-min: 180px } }

.kpi-card{
  background:#fff;
  border:1px solid #e6edf3;
  border-radius:12px;
  box-shadow:0 3px 12px rgba(13,38,76,.06);
  padding:12px 14px;
  display:flex;
  flex-direction:column;
  gap:.5rem;
  min-height:120px;        /* consistent default height */
}

/* size variants you can mix in if needed */
.kpi-sm{ min-height:100px }
.kpi-lg{ min-height:120px }

/* Internal layout */
.kpi-head{ display:flex; align-items:center; gap:.6rem; color:#425461 }
.kpi-icon{ color:var(--accent, #4F7EB3); opacity:.95; font-size:1.05rem }
.kpi-title{ margin:0; font-size:.95rem; font-weight:500; line-height:1.1 }
.kpi-sub{ margin-left:.35rem; font-size:.72rem; color:#81909a; font-weight:600 }
.kpi-value{ margin-top:.25rem; display:flex; align-items:center }
.kpi-number{ font-size: clamp(16px, 2.4vw, 22px); font-weight:700; color:#2b3a44 }

/* two-column “combined” values (in/out) */
.kpi-duo{ display:grid; grid-template-columns:1fr 1fr; gap:10px }
.kpi-pill{
  border:1px solid #e6edf3; border-radius:10px; padding:8px 10px; background:#f9fbfd;
  display:flex; flex-direction:column; gap:2px
}
.kpi-pill .label{ font-size:.75rem; color:#6b7a86; font-weight:400 }
.kpi-pill .val{ font-size: clamp(14px, 1.8vw, 20px); font-weight:500; color:#2b3a44 }

/* Live cards keep a fixed light bg (no gradient) */
.kpi-card.kpi-live{
  background:#f8fbff;
}

/* Remove borders on all KPI cards */
.kpi-card{
  border:none;                 /* was 1px solid #e6edf3 */
  position:relative;           /* needed for the accent spine */
  box-shadow:0 3px 12px rgba(13,38,76,.06);
}

/* Left accent spine (like other KPI cards across app) */
.kpi-accent::before{
  content:"";
  position:absolute;
  inset:0 auto 0 0;            /* left edge */
  width:6px;                   /* spine width */
  background: var(--accent);
  border-radius:12px 0 0 12px; /* rounded like your global cards */
  opacity:.95;
}

/* Greyed out state for clarity */
.kpi-card.kpi-live.greyed-out{
  background:#f4f4f4;
}

/* ========== Option B: labeled metric sections ==========
   Each metric type (Live activity, Visits, Crossings, Avg dwell)
   gets its own subtly tinted band with a header so the eye can
   scan one metric across zones in a single horizontal sweep. */
.kpi-section{
  border-radius:14px;
  padding:12px 14px 14px;
  margin-bottom:18px;
}
.kpi-section.tone-live{ background: rgba(108, 144, 70, .055) }

/* Override the page-level .section-head specifically inside .kpi-section. */
.kpi-section .section-head{
  display:flex; align-items:center; gap:.5rem;
  margin:0 0 10px 4px;
  padding:0; border:none; background:transparent; box-shadow:none;
  font-size:.82rem; font-weight:600; letter-spacing:.02em;
  color:#52606b; text-transform:uppercase;
}
.kpi-section .section-icon{ font-size:.95rem; color:#7a8893 }

/* Tighter card grid for the section bands — Option B trades hero
   prominence for density so 4–5 cards fit per row on a laptop. */
.kpi-section .kpi-grid.tight{
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 10px;
}
.kpi-section .kpi-grid.tight .kpi-card{
  padding:10px 12px 10px 14px;
  min-height:88px;
  gap:6px;
}
.kpi-section .kpi-grid.tight .kpi-card.kpi-accent::before{
  width:4px;
  border-radius:10px 0 0 10px;
}
.kpi-section .kpi-grid.tight .kpi-head{
  flex-direction:column; align-items:flex-start; gap:1px;
  padding-bottom:4px;
  border-bottom:1px solid #eef2f6;
}
.kpi-section .kpi-grid.tight .kpi-title{
  font-size:.88rem; font-weight:500;
}
.kpi-section .kpi-grid.tight .kpi-sub{ margin-left:0 }
.kpi-section .kpi-grid.tight .kpi-value{ min-height:28px }
.kpi-section .kpi-grid.tight .kpi-number{
  font-size: clamp(16px, 1.9vw, 22px);
}

/* ========== Section/grid glue ========== */
/* Stage holds both grids and lets them share the SAME column size */
.kpi-stage{
  /* change this once and both grids track it */
  --kpi-min: 260px;   /* card min width */
  --kpi-gap: 18px;    /* grid gap */
}

/* Each grid (parent and child) uses the same variables */
.kpi-grid{
  display:grid;
  grid-template-columns: repeat(auto-fit, minmax(var(--kpi-min), 1fr));
  gap: var(--kpi-gap);
  align-items: stretch;
  width: 100%;
}

/* full-width items (e.g., sticky filters, live stream row) */
.kpi-span-full{ grid-column: 1 / -1; }

/* a consistent visual gap between grids  */
.kpi-grid + .kpi-grid{ margin-top: 22px; }

/* Optional responsive nudge for tighter screens */
@media (max-width: 1200px){ .kpi-stage{ --kpi-min: 220px } }
@media (max-width: 700px) { .kpi-stage{ --kpi-min: 180px } }

/* Clean, consistent sticky filter box as a card */
.sticky-filter{
  position:sticky; top:0; z-index:2; background:#fff;
  border:1px solid #e6edf3; border-radius:12px; box-shadow:0 3px 12px rgba(13,38,76,.06);
  padding:12px 14px;
}

/* Legacy helpers kept (used elsewhere in file) */
.stream-counter-container{ display:flex; flex-direction:column; gap:25px; margin-top:20px; margin-left:15px; box-sizing:border-box }
/* Counters now renders labeled metric sections internally; stack them
   vertically rather than wrapping side-by-side. */
.cards-container{ display:flex; flex-direction:column; gap:0; flex:1; box-sizing:border-box }
.flex-break{ flex-basis:100%; height:0 }

.button-row{ display:flex; flex-wrap:wrap; gap:10px; margin:8px 0 14px }

/* Collapsible transition */
.roll-enter-active, .roll-leave-active{ transition: transform .35s ease, opacity .35s ease }
.roll-enter, .roll-leave-to{ transform:scaleY(0); opacity:0; transform-origin:top }

/* Small screens */
@media (max-width:768px){
  .stream-counter-container{ margin-left:0 }
  .kpi-grid{ grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)) }
}

</style>